import { Icon, Refresh } from "iconsax-reactjs";

interface ButtonProps {
  variant: "primary" | "gray" | "neutral";
  style: "solid" | "outline";
  size: "sm" | "md" | "lg";
  text: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
  customClassName?: string;
  icon?: Icon;
}

const baseClass =
  "box-border transition-colors duration-150 ease-out rounded-lg inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap";

const buttonSizeClasses = {
  sm: "h-9 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-11 px-5 text-base",
};

const iconButtonSizeClasses = {
  sm: "w-9 h-9 p-0",
  md: "w-10 h-10 p-0",
  lg: "w-11 h-11 p-0",
};

const iconSizes = {
  sm: 16,
  md: 20,
  lg: 24,
};

const appearanceClasses = {
  primary: {
    solid:
      "border border-transparent bg-primaryVar-500 hover:bg-primaryVar-600 disabled:bg-primaryVar-400 text-white",
    outline:
      "border border-primaryVar-500 disabled:border-primaryVar-400 text-primaryVar-500 hover:text-white disabled:text-primaryVar-400 hover:bg-primaryVar-500",
  },
  gray: {
    solid:
      "border border-transparent bg-gray-400 hover:bg-gray-500 disabled:bg-gray-400 text-white",
    outline:
      "border border-gray-400 hover:border-gray-500 disabled:border-gray-300 text-gray-400 hover:text-white disabled:text-gray-300",
  },
  neutral: {
    solid:
      "border border-transparent bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-black",
    outline:
      "border border-gray-200 hover:border-gray-300 disabled:border-gray-200 text-black hover:bg-gray-100",
  },
};

export default function Button({
  variant,
  style,
  size,
  text,
  loading,
  disabled,
  onClick,
  customClassName,
  icon: IconComponent,
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const isIconOnly = !text && IconComponent && !loading;

  const className = [
    baseClass,
    isIconOnly ? iconButtonSizeClasses[size] : buttonSizeClasses[size],
    appearanceClasses[variant][style],
    isDisabled && "cursor-not-allowed",
    customClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type="button"
      className={className}
      disabled={isDisabled}
      onClick={!isDisabled ? onClick : undefined}
    >
      {loading ? (
        <Refresh
          size={iconSizes[size]}
          variant="Outline"
          className="animate-spin"
        />
      ) : (
        IconComponent && <IconComponent size={iconSizes[size]} />
      )}
      {!isIconOnly && text}
    </button>
  );
}
